import React, { useState, useEffect } from "react";
import { NavLink, Outlet, useNavigate, Link, useLocation } from "react-router-dom";
import { LayoutDashboard, Building2, ClipboardList, BarChart3, Settings, LogOut, MessageSquare, Users, Menu, X, ChevronLeft, ChevronRight, Bus, GraduationCap, Inbox, Tag, TrendingUp, Briefcase, Plane, Home as HomeIcon, Landmark } from "lucide-react";
import { useApp } from "../../context/AppContext";

const AdminLayout = () => {
    const { logout, t, admin } = useApp();
    const navigate = useNavigate();
    const location = useLocation();

    // Desktop: collapsed (icon-only) vs full. Persisted.
    const [collapsed, setCollapsed] = useState(() => {
        try { return localStorage.getItem("admin_sidebar_collapsed") === "1"; } catch { return false; }
    });
    // Mobile: overlay drawer open/closed
    const [mobileOpen, setMobileOpen] = useState(false);

    // Close mobile drawer when navigating
    useEffect(() => { setMobileOpen(false); }, [location.pathname]);
    // Lock body scroll while mobile drawer is open
    useEffect(() => {
        document.body.style.overflow = mobileOpen ? "hidden" : "";
        return () => { document.body.style.overflow = ""; };
    }, [mobileOpen]);

    const toggleCollapsed = () => {
        setCollapsed((v) => {
            const next = !v;
            try { localStorage.setItem("admin_sidebar_collapsed", next ? "1" : "0"); } catch { /* noop */ }
            return next;
        });
    };

    const handleLogout = () => { logout(); navigate("/admin/login"); };

    const links = [
        { to: "/admin", end: true, icon: LayoutDashboard, label: t("admin.dashboard") },
        { to: "/admin/hotels", icon: Building2, label: t("admin.hotels") },
        { to: "/admin/transfers", icon: Bus, label: "Transfer" },
        { to: "/admin/guides", icon: GraduationCap, label: "Rehberlik" },
        { to: "/admin/guide-requests", icon: Inbox, label: "Rehberlik Talepleri" },
        { to: "/admin/service-requests", icon: Plane, label: "Uçak & Vize Talepleri" },
        { to: "/admin/hajj", icon: Landmark, label: "Hac Paketleri" },
        { to: "/admin/properties", icon: HomeIcon, label: "Emlak İlanları" },
        { to: "/admin/reservations", icon: ClipboardList, label: t("admin.reservations") },
        { to: "/admin/reviews", icon: MessageSquare, label: "Yorumlar" },
        { to: "/admin/customers", icon: Users, label: "Üye Takip" },
        { to: "/admin/occupancy", icon: BarChart3, label: t("admin.occupancy") },
        { to: "/admin/promo-codes", icon: Tag, label: "İndirim Kodları" },
        { to: "/admin/companies", icon: Briefcase, label: "Şirket Hesapları" },
        { to: "/admin/analytics", icon: TrendingUp, label: "Arama Analitiği" },
        { to: "/admin/settings", icon: Settings, label: t("admin.settings") },
    ];

    // Width classes for desktop sidebar
    const sidebarWidth = collapsed ? "lg:w-16" : "lg:w-64";

    return (
        <div className="min-h-screen bg-emerald_deep text-bone flex flex-col lg:flex-row">
            {/* Mobile top bar */}
            <header className="lg:hidden sticky top-0 z-40 bg-emerald_surface border-b border-border_green flex items-center justify-between px-3 py-2.5">
                <button
                    onClick={() => setMobileOpen(true)}
                    className="p-2 -ml-1 text-bone hover:text-gold transition-colors"
                    aria-label="Menüyü aç"
                    data-testid="admin-mobile-menu-open"
                >
                    <Menu className="w-5 h-5" />
                </button>
                <Link to="/admin" className="flex items-center gap-2" data-testid="admin-mobile-brand">
                    <img src="/assets/asis-icon.png" alt="" className="w-7 h-7 object-contain" />
                    <span className="font-serif text-base text-gold tracking-wider">{t("brand")}</span>
                </Link>
                <button
                    onClick={handleLogout}
                    className="p-2 -mr-1 text-bone hover:text-gold transition-colors"
                    aria-label="Çıkış"
                    data-testid="admin-mobile-logout"
                >
                    <LogOut className="w-5 h-5" />
                </button>
            </header>

            {/* Mobile drawer backdrop */}
            {mobileOpen && (
                <div
                    className="lg:hidden fixed inset-0 bg-black/70 z-40 backdrop-blur-sm"
                    onClick={() => setMobileOpen(false)}
                    data-testid="admin-mobile-backdrop"
                />
            )}

            {/* Sidebar — drawer on mobile, fixed on desktop */}
            <aside
                className={`
                    fixed inset-y-0 left-0 z-50 w-72 max-w-[85vw] bg-emerald_surface border-r border-border_green flex flex-col
                    transform transition-transform duration-300
                    ${mobileOpen ? "translate-x-0" : "-translate-x-full"}
                    lg:static lg:translate-x-0 lg:transition-[width] lg:duration-200 ${sidebarWidth} lg:max-w-none
                `}
                data-testid="admin-sidebar"
            >
                <div className="p-4 lg:p-5 border-b border-border_green flex items-center justify-between gap-2">
                    <Link to="/" className={`flex items-center gap-3 min-w-0 ${collapsed ? "lg:justify-center" : ""}`} data-testid="admin-brand">
                        <img src="/assets/asis-icon.png" alt="ASİS HOTELS" className="w-9 h-9 object-contain flex-shrink-0" />
                        <div className={`min-w-0 ${collapsed ? "lg:hidden" : ""}`}>
                            <div className="font-serif text-base font-semibold text-gold tracking-wider truncate">{t("brand")}</div>
                            <div className="text-[10px] uppercase tracking-[0.2em] text-bone/60">Admin</div>
                        </div>
                    </Link>
                    {/* Mobile close */}
                    <button
                        onClick={() => setMobileOpen(false)}
                        className="lg:hidden p-2 -mr-1 text-bone hover:text-gold"
                        aria-label="Menüyü kapat"
                        data-testid="admin-mobile-menu-close"
                    >
                        <X className="w-5 h-5" />
                    </button>
                </div>

                <nav className="p-3 flex-1 flex flex-col gap-1 overflow-y-auto">
                    {links.map((l) => {
                        const Icon = l.icon;
                        const slug = l.label.toLowerCase().replace(/\s+/g, "-");
                        return (
                            <NavLink
                                key={l.to}
                                to={l.to}
                                end={l.end}
                                title={collapsed ? l.label : undefined}
                                className={({ isActive }) =>
                                    `flex items-center gap-3 px-3 py-3 text-sm transition-colors ${collapsed ? "lg:justify-center lg:px-2" : ""} ${
                                        isActive ? "bg-emerald_hover border-l-2 border-gold text-gold" : "text-bone/80 hover:bg-emerald_hover hover:text-gold"
                                    }`
                                }
                                data-testid={`admin-nav-${slug}`}
                            >
                                <Icon className="w-4 h-4 flex-shrink-0" />
                                <span className={`${collapsed ? "lg:hidden" : ""} truncate`}>{l.label}</span>
                            </NavLink>
                        );
                    })}
                </nav>

                <div className="p-3 border-t border-border_green space-y-2">
                    {!collapsed && (
                        <div className="text-xs text-muted_green mb-1 truncate px-1 lg:block hidden">{admin?.email}</div>
                    )}
                    <button
                        onClick={handleLogout}
                        className={`btn-outline-gold w-full ${collapsed ? "lg:!px-2" : ""}`}
                        title={collapsed ? "Çıkış" : undefined}
                        data-testid="admin-logout-btn"
                    >
                        <LogOut className="w-4 h-4" />
                        <span className={collapsed ? "lg:hidden" : ""}>{t("admin.logout")}</span>
                    </button>
                    {/* Desktop collapse toggle */}
                    <button
                        onClick={toggleCollapsed}
                        className="hidden lg:flex w-full items-center justify-center gap-2 text-[10px] uppercase tracking-[0.18em] text-muted_green hover:text-gold transition-colors py-1.5 border border-border_green hover:border-gold/40"
                        title={collapsed ? "Paneli genişlet" : "Paneli daralt"}
                        data-testid="admin-sidebar-toggle"
                    >
                        {collapsed ? <ChevronRight className="w-3.5 h-3.5" /> : <><ChevronLeft className="w-3.5 h-3.5" /> Daralt</>}
                    </button>
                </div>
            </aside>

            <main className="flex-1 min-w-0 overflow-x-hidden">
                <div className="p-4 sm:p-6 lg:p-10">
                    <Outlet />
                </div>
            </main>
        </div>
    );
};

export default AdminLayout;
